import { LngLatBounds, LngLat } from 'maplibre-gl'
import { MutableRefObject } from 'react';
import type { Board, Place, PlaceId, PlaceGroup, Slap, SlapId, Coordinates } from '../utils/Primitives';
import { BlockPlaces } from '@ivyroot/block-places';
import { lookupSlap, deleteSlapFromBoard } from '../slaps/Slaps';


export type GetPlacesProps = {
    boardRef: MutableRefObject<Board>,
    places: PlaceGroup;
}

export function getPlaces(props: GetPlacesProps): PlaceGroup {
    const { boardRef, places } = props
    const placeStore =  boardRef.current.placeStore as PlaceGroup
    for (const key in places) {
        const placeId = Number(key)
        if (isNaN(placeId)) {
            continue
        }
        const currPlace = placeStore[placeId]
        if (currPlace) {
            places[placeId] = currPlace
        } else {
            places[placeId] = { id: placeId }
        }
        const cplace = places[placeId]
        if (cplace && (cplace.bounds == undefined || cplace.bounds == null)) {
            const cbounds = BlockPlaces.lngLatBoundsFromPlaceId(placeId)
            if (cbounds) {
                cplace.bounds = cbounds
            }
        }
    }
    return places
}

export type getPlaceParams = {
    boardRef: MutableRefObject<Board>,
    placeId: PlaceId;
}

export function getPlace(params: getPlaceParams): Place | null {
    const { boardRef, placeId } = params
    const places = {} as PlaceGroup
    places[`${placeId}`] = null
    const lookupProps = {boardRef, places}
    const placeLookup = getPlaces(lookupProps)
    return placeLookup[placeId];
}

export function getPendingSlapIdsForPlace(params: getPlaceParams): SlapId[] {
    const place = getPlace(params);
    if (!place || !place.pendingSlaps) {
        return [];
    }
    return place.pendingSlaps;
}

export function writeSlapToPlace(props: {
    boardRef: MutableRefObject<Board>,
    slap: Slap;
    placeId: PlaceId;
}) {
    const { boardRef, slap, placeId } = props;
    const currPlace = getPlace({ boardRef, placeId });
    const newSlapTime = slap.time as number;
    const currTime = currPlace?.slapTime as number | undefined;
    const currId = currPlace?.slap as number | undefined;

    if (currTime !== undefined) {
        if (newSlapTime < currTime) {
            return;
        }
        if (newSlapTime === currTime && currId !== undefined) {
            if ((slap.id as number) <= currId) {
                return;
            }
        }
    }

    const placeStore = boardRef.current.placeStore as PlaceGroup;
    const slappedPlaceBase = {
        ...currPlace,
        id: placeId,
    } as Place;
    const slapArgs = {
        slap: slap.id,
        slapTime: newSlapTime,
    };
    const existingPendingSlaps = currPlace?.pendingSlaps ? currPlace.pendingSlaps : [];
    const pendingSlapArgs = {
        pendingSlaps: [...existingPendingSlaps, slap.id]
    };
    const slappedPlace = slap.isDraft ? { ...slappedPlaceBase, ...pendingSlapArgs } : { ...slappedPlaceBase, ...slapArgs };
    placeStore[placeId] = slappedPlace;
}

export function writeSlapToPlaces(props: {
    boardRef: MutableRefObject<Board>,
    slap: Slap;
}) {
    const { boardRef, slap } = props;
    const size = slap.size || 1; // Assuming slap has a size property

    if (size === 1) {
        // If size is 1, use the existing function
        writeSlapToPlace({ ...props, placeId: slap.place as number });
    } else {
        // Get all place IDs for the given size
        const placeIds = BlockPlaces.getPlaceIdsForSizedBlockPlace(
            slap.place as number, 
            size
        );
        if (!placeIds) {
            console.error(
                `Could not get place IDs for place ${slap.place}`
            );
            return;
        }
        placeIds.forEach(placeId => {
            writeSlapToPlace({ ...props, placeId });
        });
    }
}


export function deleteSlapFromPlace(props: { boardRef: MutableRefObject<Board>, placeId: PlaceId, slapId: SlapId}) {
    const { boardRef, placeId, slapId } = props
    const currPlace = getPlace({boardRef, placeId})
    if (!currPlace) {
        console.log(`ERROR could not find place ${placeId}`)
        return
    }
    if (currPlace.slap == slapId) {
        delete currPlace.slap
    }
    if (currPlace.pendingSlaps) {
        for (const i in currPlace.pendingSlaps) {
            if (currPlace.pendingSlaps[i] == slapId) {
                delete currPlace.pendingSlaps[i]
            }
        }
    }
}

// get the count of Places in a group
export function getPlaceGroupCount(places: PlaceGroup): number {
    let count = 0
    for (const placeId in places) {
        const place = places[placeId]
        if (place || place === null) {
            count++
        }
    }
    return count
}

// create a PlaceGroup from an array of PlaceIds
export function placeGroupFromArray(placeIds: PlaceId[]): PlaceGroup {
    const placeGroup = {} as PlaceGroup
    placeIds.forEach(placeId => {
        placeGroup[placeId] = {id: placeId}
    })
    return placeGroup
}

// turn a PlaceGroup into an array of Place objects
export function placeArrayFromPlaceGroup(places: PlaceGroup): Place[] {
    const placesArray = [] as Place[]
    for (const placeId in places) {
        const thePlace = places[placeId]
        if (thePlace){
            placesArray.push(thePlace)
        }
    }
    return placesArray
}

// get the bounds containing a group of Places
export function getPlaceGroupBounds(places: PlaceGroup): LngLatBounds | null {
    var bounds = new LngLatBounds();
    for (let placeId in places) {
        const numericPlaceId = Number(placeId)
        const generatedBounds = BlockPlaces.lngLatBoundsFromPlaceId(numericPlaceId)
        if (generatedBounds) {
            bounds.extend(generatedBounds)
        }
    }
    return bounds
}

export type GetPlaceFrameProps = {
    corner: LngLat;
    inset?: number;
    size?: number;
    aspectRatio?: number; // width / height of the image
}

// get the coordinates of a square frame inside the place
export function getPlaceFrameCoordinates(props: GetPlaceFrameProps) {
    const { inset = 0.0005, size = 1, aspectRatio = 1, corner } = props;

    const placeLength = 0.01;
    const totalWidth = size * placeLength;
    const totalHeight = size * placeLength;

    // Calculate available space after insets
    let regionWidth = totalWidth - 2 * inset;
    let regionHeight = totalHeight - 2 * inset;

    // Adjust dimensions based on aspect ratio
    if (aspectRatio > 1) {
        // Wider than tall
        regionHeight = regionHeight / aspectRatio;
    } else {
        // Taller than wide
        regionWidth = regionWidth * aspectRatio;
    }

    // Get starting corner (without inset)
    const roundedLongitude = 
        Math.round(corner.lng / placeLength) * placeLength;
    const roundedLatitude = 
        Math.round(corner.lat / placeLength) * placeLength;

    // Adjust latitude for Web Mercator distortion
    const latitudeAdjustment = Math.cos(corner.lat * Math.PI / 180);

    const adjustedRoundedLatitude = roundedLatitude;
    const frameHeight = regionHeight * latitudeAdjustment;
    const frameVerticalOffset = (regionHeight - frameHeight) / 2;

    // Calculate centering offsets
    const horizontalOffset = (totalWidth - regionWidth) / 2;
    const verticalOffset = (totalHeight - regionHeight) / 2;

    // Calculate bottom and top latitude for the frame
    const bottomLatitude = adjustedRoundedLatitude + verticalOffset + frameVerticalOffset;
    const topLatitude = adjustedRoundedLatitude + verticalOffset + frameHeight + frameVerticalOffset;

    // Generate the points with centering
    const topLeft: LngLat = new LngLat(roundedLongitude + horizontalOffset, topLatitude);
    const topRight: LngLat = new LngLat(roundedLongitude + horizontalOffset + regionWidth, topLatitude);
    const bottomRight: LngLat = new LngLat(roundedLongitude + horizontalOffset + regionWidth, bottomLatitude);
    const bottomLeft: LngLat = new LngLat(roundedLongitude + horizontalOffset, bottomLatitude);

    const result: Coordinates = [
        topLeft,
        topRight,
        bottomRight,
        bottomLeft
    ];

    return result;
}



